import { useAtom, useAtomValue } from "jotai"
import { useTranslation } from "react-i18next"
import { ControlGroup } from "./ControlGroup"
import { currentMetadataAtom, currentPhotoAtom } from "@/store/photo/photos-state"
import { photoLoadedDimensionsAtom } from "@/store/editor/photo-loading"
import { formatCaptureTime } from "@/utils/date"

export const InfoPanel = () => {
	const { t } = useTranslation("components")
	return (
		<ControlGroup name={t("infoPanel.panelInfo")} expandKey="info" isExpanded>
			<InfoDisplay />
		</ControlGroup>
	)
}

const InfoDisplay = () => {
	const { t } = useTranslation("components")
	const [currentPhoto] = useAtom(currentPhotoAtom)
	const [metadata] = useAtom(currentMetadataAtom)
	const loadedDimensions = useAtomValue(photoLoadedDimensionsAtom)

	const dimensions =
		loadedDimensions ??
		(metadata?.auxiliaryData?.hasComputedDimensions && metadata?.width && metadata?.height
			? [metadata.width, metadata.height]
			: null)

	if (!currentPhoto || !metadata) {
		return null
	}

	const fileSizeMB = (metadata.size / (1024 * 1024)).toFixed(2)
	const shutterSpeedSec = metadata.shutterSpeed ? Math.round(1 / metadata.shutterSpeed) : 0
	const hasExifDetails = Boolean(
		metadata.focalLength || metadata.shutterSpeed || metadata.fNumber || metadata.iso,
	)

	const make = metadata.cameraMake?.trim()
	const model = metadata.cameraModel?.trim()
	const fullCameraModel =
		make && model
			? model.startsWith(make)
				? model
				: `${make} ${model}`
			: (model ?? make ?? t("infoPanel.unknownCamera"))
	const createdDateDisplay = formatCaptureTime(metadata.dateCreated, metadata.dateCreatedOffset)

	const renderDimensionsInfo = () => {
		if (dimensions) {
			return `${dimensions[0]} x ${dimensions[1]} · `
		} else {
			return (
				<>
					<span className="relative top-px mr-1 inline-block h-3 w-[64px] rounded-sm bg-transparent"></span>
				</>
			)
		}
	}

	return (
		<div className="mt-2 flex flex-col space-y-2 rounded-md border border-base-700 p-2 text-xs text-base-300">
			<div className="flex flex-col space-y-1">
				<div>{fullCameraModel}</div>
				<div>{metadata.lensModel}</div>
				<div className="flex flex-row items-center justify-between">
					<div className="flex flex-row items-center">
						{renderDimensionsInfo()} {fileSizeMB} {t("infoPanel.mb")}
					</div>
					<div className="h-fit rounded-sm bg-base-600 px-1.5 py-0.5 font-bold uppercase">
						{metadata.extension}
					</div>
				</div>
			</div>

			{hasExifDetails ? (
				<>
					<div className="flex h-px w-full bg-base-700"></div>
					<div className="flex flex-col space-y-1">
						{!!metadata.focalLength && (
							<div className="grid grid-cols-2">
								<div>{t("infoPanel.focalLength")}</div>
								<div>
									{t("infoPanel.mm", { value: metadata.focalLength.toFixed(0) })}
								</div>
							</div>
						)}
						{!!metadata.shutterSpeed && (
							<div className="grid grid-cols-2">
								<div>{t("infoPanel.shutterSpeed")}</div>
								<div>
									{t("infoPanel.shutterSpeedValue", { speed: shutterSpeedSec })}
								</div>
							</div>
						)}
						{!!metadata.fNumber && (
							<div className="grid grid-cols-2">
								<div>{t("infoPanel.aperture")}</div>
								<div>
									{t("infoPanel.fNumber", { value: metadata.fNumber.toFixed(1) })}
								</div>
							</div>
						)}
						{!!metadata.iso && (
							<div className="grid grid-cols-2">
								<div>{t("infoPanel.iso")}</div>
								<div>{metadata.iso}</div>
							</div>
						)}
					</div>
					<div className="flex h-px w-full bg-base-700"></div>
				</>
			) : (
				<div className="flex h-px w-full bg-base-700"></div>
			)}

			<div className="flex flex-col space-y-1">
				<div className="truncate">{currentPhoto.fileName}</div>
				<div>{t("infoPanel.capturedAt", { date: createdDateDisplay })}</div>
			</div>
		</div>
	)
}
